import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import { useAuth } from '../../auth/useAuth';
import { resolveBrandTheme } from '../../../lib/portalReport/config';
import { PortalBrandProvider, type PortalBrand } from './PortalBrandContext';
import { PortalModeProvider } from './PortalModeContext';
import { fetchPortalMe } from './portalApi';
import { PortalFinish } from './PortalFinish';
import { PortalHome } from './PortalHome';
import { PortalShell } from './PortalShell';
import { PortalSignIn } from './PortalSignIn';
import {
  ReportGooglePage,
  ReportMetaPage,
  ReportOverviewPage,
  ReportWebsitePage,
  ReportClubwisePage,
} from './report/ReportPages';
import { LibraryPage } from './library/LibraryPage';
import { LibraryDocumentPage } from './library/LibraryDocumentPage';
import { PortalInspirationPage } from './inspiration/PortalInspirationPage';
import { LeadConversationsRedirect, PortalLeadsPage } from './kyckConnect/PortalLeadsPage';
import { PortalSetupPage } from './kyckConnect/PortalSetupPage';
import { PortalDashboardPage } from './kyckConnect/PortalDashboardPage';
import { PortalReportingPage } from './kyckConnect/PortalReportingPage';
import { PORTAL_SETUP_SPEED_TO_LEAD_PATH } from '../../../lib/kyckConnect/portalLinks';
import { PortalAssetsPage } from './assets/PortalAssetsPage';
import { PortalAssetDetailPage } from './assets/PortalAssetDetailPage';
import { PortalAssetsUploadPage } from './assets/PortalAssetsUploadPage';
import { ClubProfilePage } from './social/ClubProfilePage';
import { ComposerPage } from './social/ComposerPage';
import { InboxPage } from './social/InboxPage';
import { PostsPage } from './social/PostsPage';

// CLIENT PORTAL — the /portal/* route tree.
//
// ⚠️⚠️ RENDERED OUTSIDE SignedInGuard AND AppShell (see App.tsx). Those are the
// STAFF guard and the STAFF chrome: SignedInGuard enforces the kyck domain, so
// running a client through it would reject them, and AppShell is the agency
// sidebar, which a client must never see. The public preview / survey /
// hosted-report pages are the precedent for a route tree living outside both.
//
// ⚠️ The guard here is COSMETIC — it decides which screen to draw, not who may
// read what. Every byte of client data comes from /api/portal behind
// requirePortalAuth, which resolves the scope server-side from the portal user's
// own record. A tampered client cannot widen anything by editing state in the
// browser, because the browser never had a say.

export function PortalRoutes() {
  const { user, loading } = useAuth();
  const brand = usePortalBrandFromMe(!!user);

  return (
    <PortalBrandProvider brand={brand}>
    {/* ⚠️ ONE MODE FOR THE WHOLE PORTAL, held above the routes. The report used
        to own it, which was fine while the shell had no look to be out of step
        with — once the shell took the report's tokens, a client switching to
        light would have had a light report inside a dark shell, and the choice
        would have looked forgotten on the way back to the home page. */}
    <PortalModeProvider>
    <Routes>
      {/*
        /portal/finish is OUTSIDE the signed-in check on purpose: completing an
        email link is exactly the moment when there is no session yet, so
        gating it behind one would make the link impossible to use.
      */}
      <Route path="finish" element={<PortalFinish />} />
      {/*
        Phase 1 — the report. Behind the same signed-in check as the home page;
        the `reporting` PERMISSION is enforced SERVER-SIDE on every fetch, and a
        403 renders as "reports aren't enabled" rather than an error. The guard
        here only decides which screen to draw.
      */}
      <Route
        path="report"
        element={signedIn(loading, user, <ReportOverviewPage />)}
      />
      <Route
        path="report/meta"
        element={signedIn(loading, user, <ReportMetaPage />)}
      />
      <Route
        path="report/google"
        element={signedIn(loading, user, <ReportGooglePage />)}
      />
      <Route
        path="report/website"
        element={signedIn(loading, user, <ReportWebsitePage />)}
      />
      <Route
        path="report/clubwise"
        element={signedIn(loading, user, <ReportClubwisePage />)}
      />
      {/*
        The LIBRARY (2026-09-23). Paths match the nav's live children in
        src/lib/portalNav.ts. ⚠️ No permission gate here or on the server: the
        audience alone decides what a section holds (brief §0 #2).
      */}
      <Route
        path="strategy/monthly"
        element={signedIn(loading, user, <LibraryPage section="strategy.monthly" />)}
      />
      <Route
        path="strategy/alternate"
        element={signedIn(loading, user, <LibraryPage section="strategy.alternate" />)}
      />
      {/* ⚠️ Round 6 §3: the ONE strategy section for non-fitness-franchise
          clients. A viewer whose nav lacks it is served an empty section by the
          server's visibility check, never someone else's decks. */}
      <Route
        path="strategy/campaigns"
        element={signedIn(loading, user, <LibraryPage section="strategy.campaigns" />)}
      />
      <Route
        path="college"
        element={signedIn(loading, user, <LibraryPage section="college" />)}
      />
      {/* Inspiration (Run I) - VIEW ONLY. The server applies each entry's audience; a viewer with none sees the empty sentence. */}
      <Route path="inspiration" element={signedIn(loading, user, <PortalInspirationPage />)} />
      {/* Lead conversations (2026-10-17): the club's KyckConnect conversations. Scope is enforced server-side; the nav item shows only when the viewer has a bot. */}
      <Route path="lead-conversations" element={signedIn(loading, user, <LeadConversationsRedirect />)} />
      {/* KyckConnect (Phase 2): the club's Leads, the shared split view in the Kyck Design System look. Scope is enforced server-side. */}
      <Route path="kyckconnect" element={<Navigate to="/portal/kyckconnect/dashboard" replace />} />
      <Route path="kyckconnect/dashboard" element={signedIn(loading, user, <PortalDashboardPage />)} />
      <Route path="kyckconnect/reporting" element={signedIn(loading, user, <PortalReportingPage />)} />
      <Route path="kyckconnect/leads" element={signedIn(loading, user, <PortalLeadsPage />)} />
      {/* KyckConnect (Phase 4): the club's Setup. Scope is enforced server-side; saving needs the kyckconnect_setup permission. */}
      <Route path="kyckconnect/setup" element={signedIn(loading, user, <PortalSetupPage />)} />
      {/* The old "Who we call" address: its call list, messages and ring settings live in Setup, Speed to Lead. */}
      <Route path="speed-to-lead" element={<Navigate to={PORTAL_SETUP_SPEED_TO_LEAD_PATH} replace />} />
      {/* ⚠️ Round 6 §4: under /portal/reports/, NOT /portal/report/, so the
          Reports link does not also light up on a page that is not the report. */}
      <Route
        path="reports/custom"
        element={signedIn(loading, user, <LibraryPage section="reports.custom" />)}
      />
      {/*
        ONE library document (Round 3). ⚠️ The only way a portal document opens:
        the page re-checks the viewer on every visit, and the document's old
        /r/:token link now answers "this is in the portal".
      */}
      {/*
        APPROVED ASSETS (spec §8, commit 12). Linked from the nav for every
        portal user since 98c1fb05. The `assets` PERMISSION is enforced server-side
        on every call; a viewer without it is told so, not shown an error.
        ⚠️ `upload` is declared before `:assetId` so it is not read as an id.
      */}
      <Route path="assets" element={signedIn(loading, user, <PortalAssetsPage />)} />
      <Route path="assets/upload" element={signedIn(loading, user, <PortalAssetsUploadPage />)} />
      <Route path="assets/:assetId" element={signedIn(loading, user, <PortalAssetDetailPage />)} />
      {/* KyckSocial (2026-10-13 pilot). The `social` permission is enforced server-side. */}
      {/* ⚠️ /portal/social and /portal/social/new are LIVE PILOT URLS (bookmarks, and the
          composer used to navigate to the first): keep them as redirects. */}
      <Route path="social" element={<RedirectKeepSearch to="/portal/social/posts" />} />
      <Route path="social/new" element={<RedirectKeepSearch to="/portal/social/posts/new" />} />
      <Route path="social/posts" element={signedIn(loading, user, <PostsPage />)} />
      <Route path="social/posts/new" element={signedIn(loading, user, <ComposerPage />)} />
      <Route path="social/posts/:postId" element={signedIn(loading, user, <ComposerPage />)} />
      <Route path="social/inbox" element={signedIn(loading, user, <InboxPage />)} />
      <Route path="social/profile" element={signedIn(loading, user, <ClubProfilePage />)} />
      <Route
        path="library/:entryId"
        element={signedIn(loading, user, <LibraryDocumentPage />)}
      />
      <Route
        path="*"
        element={
          loading ? (
            <PortalShell>
              <CenteredSpinner />
            </PortalShell>
          ) : user ? (
            <PortalHome />
          ) : (
            <PortalSignIn />
          )
        }
      />
    </Routes>
    </PortalModeProvider>
    </PortalBrandProvider>
  );
}

/**
 * The brand for the whole portal, from `/api/portal/me`.
 *
 * ⚠️ SHARES `/me`'s react-query key with PortalHome, so this adds NO extra
 * request — the home page and the theme read one cached response.
 *
 * ⚠️ Disabled when signed out, because `/me` 403s without a session and there is
 * no brand to know yet. An error, a 403 and a pending fetch all resolve to the
 * kyck default, which is the honest "we don't know who you are" rendering rather
 * than a flash of the wrong brand.
 */
function usePortalBrandFromMe(signedIn: boolean): PortalBrand {
  const me = useQuery({
    queryKey: ['portal-me'],
    queryFn: fetchPortalMe,
    enabled: signedIn,
    retry: false,
  });
  const brandAccountId = me.data?.brandAccountId ?? null;
  const brandName = me.data?.brandName ?? null;
  return { theme: resolveBrandTheme(brandAccountId, brandName), brandName };
}

/** The shared signed-in gate for the report routes. Cosmetic — see the header. */
function RedirectKeepSearch({ to }: { to: string }): ReactElement {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

function signedIn(loading: boolean, user: unknown, page: ReactElement): ReactElement {
  if (loading) {
    return (
      <PortalShell>
        <CenteredSpinner />
      </PortalShell>
    );
  }
  return user ? page : <PortalSignIn />;
}
